import { useEffect, useState } from "react";
import { internalApi, type SupportScriptRecord } from "../../lib/internal-api.js";

// Раздел «Скрипты» в панели шаблонов: личные текстовые заготовки саппорта.
// В отличие от трастовых шаблонов (создаёт только админ), скрипты каждый
// пользователь создаёт и правит сам; отправка — одним кликом в открытый чат.

interface SupportScriptsPanelProps {
  wsUrl: string;
  canSend: boolean;
  onSendText(text: string): void;
}

interface ScriptDraft {
  title: string;
  content: string;
  description: string;
}

const EMPTY_DRAFT: ScriptDraft = { title: "", content: "", description: "" };

export const SupportScriptsPanel = ({ wsUrl, canSend, onSendText }: SupportScriptsPanelProps) => {
  const [scripts, setScripts] = useState<SupportScriptRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ScriptDraft>(EMPTY_DRAFT);
  const [query, setQuery] = useState("");
  const [sentId, setSentId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void internalApi
      .listSupportScripts(wsUrl)
      .then((payload) => {
        if (!cancelled) {
          setScripts(payload.scripts);
        }
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "Не удалось загрузить скрипты");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [wsUrl]);

  const openCreate = (): void => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormOpen(true);
    setError(null);
  };

  const openEdit = (script: SupportScriptRecord): void => {
    setEditingId(script.scriptId);
    setDraft({
      title: script.title,
      content: script.content,
      description: script.description ?? ""
    });
    setFormOpen(true);
    setError(null);
  };

  const submit = async (): Promise<void> => {
    const title = draft.title.trim();
    const content = draft.content.trim();
    if (title.length === 0 || content.length === 0) {
      setError("Заполните название и содержание.");
      return;
    }
    setBusyId(editingId ?? "new");
    setError(null);
    try {
      if (editingId) {
        const { script } = await internalApi.updateSupportScript(
          editingId,
          { title, content, description: draft.description.trim() || null },
          wsUrl
        );
        setScripts((current) =>
          current.map((entry) => (entry.scriptId === script.scriptId ? script : entry))
        );
      } else {
        const { script } = await internalApi.createSupportScript(
          {
            title,
            content,
            ...(draft.description.trim() ? { description: draft.description.trim() } : {})
          },
          wsUrl
        );
        setScripts((current) => [script, ...current]);
      }
      setFormOpen(false);
      setEditingId(null);
      setDraft(EMPTY_DRAFT);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось сохранить скрипт");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (script: SupportScriptRecord): Promise<void> => {
    if (!window.confirm(`Удалить скрипт «${script.title}»?`)) {
      return;
    }
    setBusyId(script.scriptId);
    setError(null);
    try {
      await internalApi.deleteSupportScript(script.scriptId, wsUrl);
      setScripts((current) => current.filter((entry) => entry.scriptId !== script.scriptId));
      if (editingId === script.scriptId) {
        setFormOpen(false);
        setEditingId(null);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось удалить скрипт");
    } finally {
      setBusyId(null);
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const visible = normalizedQuery
    ? scripts.filter((script) =>
        [script.title, script.description ?? "", script.content]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery)
      )
    : scripts;

  return (
    <div className="support-scripts">
      <div className="support-scripts__toolbar">
        <div className="search support-scripts__search">
          <i className="ic ic-search" aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск по скриптам"
            aria-label="Поиск по скриптам"
          />
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => (formOpen && !editingId ? setFormOpen(false) : openCreate())}
        >
          <i className={formOpen && !editingId ? "ic ic-close" : "ic ic-plus"} aria-hidden="true" />{" "}
          {formOpen && !editingId ? "Скрыть форму" : "Создать скрипт"}
        </button>
      </div>

      {error ? <div className="support-scripts__error">{error}</div> : null}
      {loading ? <div className="support-scripts__hint">Загружаем…</div> : null}

      {formOpen ? (
        <div className="support-scripts__form">
          <input
            value={draft.title}
            onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
            placeholder="Название скрипта"
            maxLength={100}
          />
          <textarea
            value={draft.content}
            onChange={(event) =>
              setDraft((current) => ({ ...current, content: event.target.value }))
            }
            placeholder="Содержание — этот текст уйдёт юзеру"
            rows={4}
            maxLength={4000}
          />
          <textarea
            value={draft.description}
            onChange={(event) =>
              setDraft((current) => ({ ...current, description: event.target.value }))
            }
            placeholder="Описание для себя: что это за скрипт и когда его слать (юзер его не видит)"
            rows={2}
            maxLength={1000}
          />
          <div className="support-scripts__form-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => void submit()}
              disabled={busyId !== null}
            >
              {busyId !== null
                ? "Сохраняем…"
                : editingId
                  ? "Сохранить изменения"
                  : "Создать скрипт"}
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                setFormOpen(false);
                setEditingId(null);
                setDraft(EMPTY_DRAFT);
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      ) : null}

      {!loading && scripts.length === 0 && !formOpen ? (
        <div className="support-scripts__hint">
          Скриптов пока нет. Создайте первый — это ваши личные текстовые заготовки,
          отправляются в чат одним кликом.
        </div>
      ) : null}
      {!loading && scripts.length > 0 && visible.length === 0 ? (
        <div className="support-scripts__hint">Ничего не найдено.</div>
      ) : null}

      <div className="support-scripts__list">
        {visible.map((script) => (
          <div className="support-scripts__item" key={script.scriptId}>
            <div className="support-scripts__item-body">
              <strong className="support-scripts__item-title">{script.title}</strong>
              {script.description ? (
                <span className="support-scripts__item-description">{script.description}</span>
              ) : null}
              <span className="support-scripts__item-preview">{script.content}</span>
            </div>
            <div className="support-scripts__item-actions">
              <button
                type="button"
                className="button button--primary support-scripts__send"
                disabled={!canSend || busyId !== null}
                title={canSend ? "Отправить в открытый чат" : "Сначала откройте чат"}
                onClick={() => {
                  onSendText(script.content);
                  setSentId(script.scriptId);
                  window.setTimeout(() => setSentId(null), 1500);
                }}
              >
                {sentId === script.scriptId ? "Отправлено ✓" : "Отправить"}
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label="Редактировать скрипт"
                title="Редактировать"
                disabled={busyId !== null}
                onClick={() => openEdit(script)}
              >
                <i className="ic ic-edit" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label="Удалить скрипт"
                title="Удалить"
                disabled={busyId !== null}
                onClick={() => void remove(script)}
              >
                <i className="ic ic-delete" aria-hidden="true" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
